/**
 * Apple Reminders background sync utility.
 *
 * Reads device EventKit reminders (iOS Reminders app) via expo-calendar,
 * performs a delta sync against the backend (only items modified since the
 * last sync timestamp), and sends them in batches of 50.  Fires a local push
 * notification when the sync completes.
 *
 * Background task follows the same pattern as appleCalendarSync.ts:
 *   - Lazy-load expo-task-manager + expo-background-task
 *   - defineTask MUST be called at the top level (before AppRegistry)
 *   - minimumInterval: 1 hour
 *
 * IMPORTANT: Import this file from index.ts so defineTask runs at startup.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { store } from '../store';
import debug from './debug';

export const APPLE_REMINDERS_SYNC_TASK = 'ELEPLAN_APPLE_REMINDERS_SYNC';
export const APPLE_REMINDERS_LAST_SYNC_KEY = (projectId: string) =>
  `@eleplan/apple_reminders_last_sync_${projectId}`;
export const APPLE_REMINDERS_SYNC_ENABLED_KEY = (projectId: string) =>
  `@eleplan/apple_reminders_sync_enabled_${projectId}`;

export interface AppleReminderItem {
  id: string;
  title: string;
  notes: string | null;
  dueDate: string | null;
  completed: boolean;
  completionDate: string | null;
  reminderListId: string | null;
  reminderListName: string | null;
}

// ── Lazy-load native modules ──────────────────────────────────────────────────

let TaskManager: typeof import('expo-task-manager') | null = null;
let BackgroundTask: typeof import('expo-background-task') | null = null;

if (Platform.OS !== 'web') {
  try {
    TaskManager = require('expo-task-manager') as typeof import('expo-task-manager');
    BackgroundTask = require('expo-background-task') as typeof import('expo-background-task');
  } catch {
    // Native modules not compiled in — background Apple Reminders sync disabled.
  }
}

// ── Task definition (must be top-level) ──────────────────────────────────────

if (TaskManager && BackgroundTask) {
  const _bt = BackgroundTask;
  try {
    TaskManager.defineTask(APPLE_REMINDERS_SYNC_TASK, async () => {
      try {
        debug.log('ui', 'Background Apple Reminders sync task started');
        await runAppleRemindersSyncAllProjects();
        debug.log('ui', 'Background Apple Reminders sync task completed');
        return _bt.BackgroundTaskResult.Success;
      } catch (err) {
        debug.warn('Background Apple Reminders sync task failed', err);
        return _bt.BackgroundTaskResult.Failed;
      }
    });
  } catch {
    // defineTask failed — likely Expo Go without native modules.
  }
}

// ── Registration helpers ──────────────────────────────────────────────────────

export async function registerAppleRemindersTask(): Promise<void> {
  if (Platform.OS !== 'ios' || !BackgroundTask || !TaskManager) return;
  try {
    const status = await BackgroundTask.getStatusAsync();
    if (
      status === BackgroundTask.BackgroundTaskStatus.Restricted
    ) {
      // System-level "no background tasks allowed" (sim, or Background App
      // Refresh disabled). Expected, not actionable — log at info instead
      // of warn to keep the dev console quiet across re-launches.
      debug.info('background-task', 'Apple Reminders background task unavailable — skipping registration', { status });
      return;
    }
    const alreadyRegistered = await TaskManager.isTaskRegisteredAsync(APPLE_REMINDERS_SYNC_TASK);
    if (alreadyRegistered) return;
    await BackgroundTask.registerTaskAsync(APPLE_REMINDERS_SYNC_TASK, {
      minimumInterval: 60 * 60, // 1 hour
    });
    debug.log('ui', 'Apple Reminders background task registered');
  } catch (err) {
    debug.warn('Failed to register Apple Reminders background task', err);
  }
}

export async function unregisterAppleRemindersTask(): Promise<void> {
  if (Platform.OS !== 'ios' || !BackgroundTask || !TaskManager) return;
  try {
    const registered = await TaskManager.isTaskRegisteredAsync(APPLE_REMINDERS_SYNC_TASK);
    if (registered) {
      await BackgroundTask.unregisterTaskAsync(APPLE_REMINDERS_SYNC_TASK);
      debug.log('ui', 'Apple Reminders background task unregistered');
    }
  } catch (err) {
    debug.warn('Failed to unregister Apple Reminders background task', err);
  }
}

// ── Core sync helpers ─────────────────────────────────────────────────────────

async function runAppleRemindersSyncAllProjects(): Promise<void> {
  const state = store.getState();
  const projects: any[] = Object.values((state as any).projects?.list ?? {});
  for (const project of projects) {
    const projectId = String(project.id ?? project.project_id);
    if (!projectId) continue;
    const syncEnabled = await AsyncStorage.getItem(APPLE_REMINDERS_SYNC_ENABLED_KEY(projectId));
    if (syncEnabled !== 'true') continue;
    try {
      await runAppleRemindersSync(projectId);
    } catch (err) {
      debug.warn('Apple Reminders sync failed for project', { projectId, err });
    }
  }
}

/**
 * Run an Apple Reminders sync for a specific project.
 * Exported so RemindersScreen can trigger "Sync Now".
 */
export async function runAppleRemindersSync(
  projectId: string,
  fullSync = false,
): Promise<{ synced: number; skipped: boolean; noLists?: boolean }> {
  if (Platform.OS !== 'ios') return { synced: 0, skipped: true };

  let Calendars: typeof import('expo-calendar') | null = null;
  try {
    Calendars = require('expo-calendar') as typeof import('expo-calendar');
  } catch {
    debug.warn('Apple Reminders sync: expo-calendar not available');
    return { synced: 0, skipped: true };
  }

  // Separate permission required for Reminders (different from Calendar)
  const { status } = await Calendars.getRemindersPermissionsAsync();
  debug.log('ui', 'Apple Reminders sync: permission status', { status, projectId, fullSync });
  if (status !== 'granted') return { synced: 0, skipped: true };

  // Delta window — only applies when fullSync=false AND a prior sync timestamp exists.
  // Manual "Sync Now" always passes fullSync=true so the delta filter is never applied
  // for user-initiated syncs, which prevents issues with stale lastModifiedDate values.
  const lastSyncKey = APPLE_REMINDERS_LAST_SYNC_KEY(projectId);
  const lastSyncStr = fullSync ? null : await AsyncStorage.getItem(lastSyncKey);
  const lastSync = lastSyncStr ? new Date(lastSyncStr) : null;
  debug.log('ui', 'Apple Reminders sync: delta window', { fullSync, lastSync: lastSync?.toISOString() ?? 'none' });

  const candidateResult = await getAppleRemindersCandidatesRaw(Calendars);
  if (candidateResult.noLists) {
    return { synced: 0, skipped: false, noLists: true };
  }
  const now = new Date();
  const all = candidateResult.raw;

  debug.log('ui', 'Apple Reminders sync: fetched from EventKit', { total: all.length });

  // Delta filter: only applies when fullSync=false and a lastSync timestamp exists.
  const toSync = lastSync
    ? all.filter((r) => {
        const modified = (r as any).lastModifiedDate;
        if (!modified) return true;
        return new Date(modified) > lastSync;
      })
    : all;

  debug.log('ui', 'Apple Reminders sync: after delta filter', {
    toSync: toSync.length,
    filtered: all.length - toSync.length,
    lastSync: lastSync?.toISOString() ?? 'none',
  });

  if (toSync.length === 0) {
    return { synced: 0, skipped: false };
  }

  const mapped = mapReminderItems(toSync, candidateResult.reminderLists);

  // Send in chunks of 50
  const CHUNK_SIZE = 50;
  const chunks: typeof mapped[] = [];
  for (let i = 0; i < mapped.length; i += CHUNK_SIZE) {
    chunks.push(mapped.slice(i, i + CHUNK_SIZE));
  }

  let totalSynced = 0;
  for (let i = 0; i < chunks.length; i++) {
    try {
      const res = await (store.dispatch as any)(
        syncAppleRemindersChunk(projectId, chunks[i], i + 1, chunks.length),
      );
      totalSynced += res?.synced ?? 0;
    } catch (err) {
      debug.warn('Apple Reminders chunk sync failed', { chunk: i + 1, err });
    }
  }

  await AsyncStorage.setItem(APPLE_REMINDERS_SYNC_ENABLED_KEY(projectId), 'true');
  await AsyncStorage.setItem(lastSyncKey, now.toISOString());

  if (totalSynced > 0) {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: 'Apple Reminders synced',
          body: `${totalSynced} reminder${totalSynced !== 1 ? 's' : ''} imported`,
        },
        trigger: null,
      });
    } catch {
      // Non-critical
    }
  }

  debug.log('ui', 'Apple Reminders sync complete', { projectId, totalSynced, chunks: chunks.length });
  return { synced: totalSynced, skipped: false };
}

export async function getAppleRemindersCandidates(): Promise<{
  reminders: AppleReminderItem[];
  skipped: boolean;
  noLists?: boolean;
}> {
  if (Platform.OS !== 'ios') return { reminders: [], skipped: true };

  let Calendars: typeof import('expo-calendar') | null = null;
  try {
    Calendars = require('expo-calendar') as typeof import('expo-calendar');
  } catch {
    debug.warn('Apple Reminders candidates: expo-calendar not available');
    return { reminders: [], skipped: true };
  }

  const { status } = await Calendars.getRemindersPermissionsAsync().catch(() => ({ status: 'denied' }));
  if (status !== 'granted') {
    return { reminders: [], skipped: true };
  }

  const candidateResult = await getAppleRemindersCandidatesRaw(Calendars);
  if (candidateResult.noLists) {
    return { reminders: [], skipped: false, noLists: true };
  }

  const reminders = mapReminderItems(candidateResult.raw, candidateResult.reminderLists);
  return { reminders, skipped: false };
}

export async function importAppleRemindersSelection(
  projectId: string,
  reminders: AppleReminderItem[],
): Promise<{ synced: number; skipped: boolean }> {
  if (Platform.OS !== 'ios') return { synced: 0, skipped: true };
  if (!Array.isArray(reminders) || reminders.length === 0) return { synced: 0, skipped: false };

  const now = new Date();
  const CHUNK_SIZE = 50;
  const chunks: AppleReminderItem[][] = [];
  for (let i = 0; i < reminders.length; i += CHUNK_SIZE) {
    chunks.push(reminders.slice(i, i + CHUNK_SIZE));
  }

  let totalSynced = 0;
  for (let i = 0; i < chunks.length; i++) {
    try {
      const res = await (store.dispatch as any)(
        syncAppleRemindersChunk(projectId, chunks[i], i + 1, chunks.length),
      );
      totalSynced += res?.synced ?? 0;
    } catch (err) {
      debug.warn('Apple Reminders selected chunk sync failed', { chunk: i + 1, err });
    }
  }

  const lastSyncKey = APPLE_REMINDERS_LAST_SYNC_KEY(projectId);
  await AsyncStorage.setItem(APPLE_REMINDERS_SYNC_ENABLED_KEY(projectId), 'true');
  await AsyncStorage.setItem(lastSyncKey, now.toISOString());

  if (totalSynced > 0) {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: 'Apple Reminders imported',
          body: `${totalSynced} reminder${totalSynced !== 1 ? 's' : ''} imported`,
        },
        trigger: null,
      });
    } catch {
      // Non-critical
    }
  }

  return { synced: totalSynced, skipped: false };
}

async function getAppleRemindersCandidatesRaw(Calendars: typeof import('expo-calendar')): Promise<{
  raw: any[];
  reminderLists: any[];
  noLists?: boolean;
}> {
  const reminderLists = await Calendars.getCalendarsAsync(Calendars.EntityTypes.REMINDER);
  debug.log('ui', 'Apple Reminders sync: reminder lists', { count: reminderLists.length, lists: reminderLists.map((l) => l.title) });
  if (!reminderLists.length) {
    debug.warn('Apple Reminders sync: no reminder lists found — EventKit returned empty (simulator?)');
    return { raw: [], reminderLists: [], noLists: true };
  }

  const now = new Date();
  const windowStart = new Date(now.getFullYear() - 5, 0, 1);
  const windowEnd = new Date(now.getFullYear() + 5, 11, 31);
  const calendarIds = reminderLists.map((l) => l.id);

  let all: any[] = [];
  try {
    all = await (Calendars.getRemindersAsync(
      calendarIds,
      null as unknown as typeof Calendars.ReminderStatus.INCOMPLETE,
      null as unknown as Date,
      null as unknown as Date,
    ) as Promise<any[]>);
  } catch (err) {
    debug.warn('Apple Reminders sync: null-status/null-date fetch failed, falling back to wide window', err);
    const [fb1, fb2] = await Promise.all([
      Calendars.getRemindersAsync(calendarIds, Calendars.ReminderStatus.INCOMPLETE, windowStart, windowEnd).catch(() => [] as any[]),
      Calendars.getRemindersAsync(calendarIds, Calendars.ReminderStatus.COMPLETED, windowStart, windowEnd).catch(() => [] as any[]),
    ]);
    all = [...fb1, ...fb2];
  }

  debug.log('ui', 'Apple Reminders sync: fetched from EventKit', { total: all.length });
  return { raw: all, reminderLists };
}

function mapReminderItems(reminders: any[], reminderLists: any[]): AppleReminderItem[] {
  return reminders.map((r) => {
    const list = reminderLists.find((l) => l.id === (r as any).calendarId);
    return {
      id: r.id,
      title: r.title || 'Reminder',
      notes: r.notes || null,
      dueDate: r.dueDate ? new Date(r.dueDate).toISOString() : null,
      completed: r.completed ?? false,
      completionDate: r.completionDate ? new Date(r.completionDate).toISOString() : null,
      reminderListId: (r as any).calendarId || null,
      reminderListName: list?.title || null,
    };
  });
}

function syncAppleRemindersChunk(
  projectId: string,
  reminders: any[],
  chunk: number,
  totalChunks: number,
) {
  return async (_dispatch: any): Promise<any> => {
    const { apiPost } = require('../utilities/apiClient');
    const state = store.getState();
    const accountId = (state as any).session?.account?.account_id as string;
    const cognitoId = (state as any).authentication?.user?.cognito_id as string;
    if (!accountId || !cognitoId) return { synced: 0 };
    return apiPost(`/prompts/apple-reminders-sync/${projectId}/${accountId}/${cognitoId}`, {
      reminders,
      chunk,
      totalChunks,
    });
  };
}
